import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, ShieldBan, ShieldCheck } from 'lucide-react'
import { env } from '@/lib/env'
import { endOfDayParam, formatDateTimeCompact, startOfDayParam } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { pluralize } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminDriver, PeopleFilters } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterDate, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { Badge, StatusBadge } from '@/components/ui/StatusBadge'
import { AddDriverDialog } from './AddDriverDialog'
import { activateDriver, fetchDrivers, suspendDriver } from './api'

interface PendingAction {
  driver: AdminDriver
  action: 'suspend' | 'activate'
}

const DEFAULT_FILTERS = {
  search: '',
  enabled: '',
  createdFrom: '',
  createdTo: '',
}

export function DriversPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  useEffect(() => {
    document.title = `Drivers · ${env.appName} Operations`
  }, [])

  const filters = useMemo<PeopleFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      enabled: table.filters.enabled === '' ? undefined : table.filters.enabled === 'true',
      createdFrom: table.filters.createdFrom ? startOfDayParam(table.filters.createdFrom) : undefined,
      createdTo: table.filters.createdTo ? endOfDayParam(table.filters.createdTo) : undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const driversQuery = useQuery({
    queryKey: queryKeys.drivers.list(filters),
    queryFn: () => fetchDrivers(filters),
    placeholderData: (previous) => previous,
  })

  const statusMutation = useAdminMutation({
    mutationFn: ({ driver, action }: PendingAction) =>
      action === 'suspend' ? suspendDriver(driver.id) : activateDriver(driver.id),
    successTitle: 'Driver updated',
    // A suspended driver disappears from every assignment control, so the
    // eligible list has to be re-read too.
    invalidate: [queryKeys.drivers.all, queryKeys.dashboard.all],
    errorTitle: 'Could not update the driver',
    onSuccess: () => setPending(null),
  })

  const columns = useMemo<ColumnDef<AdminDriver, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Driver',
        meta: { sortKey: 'name', mobileLabel: 'Driver' },
        cell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minWidth: 0 }}>
            <Avatar name={row.original.name} src={row.original.profileImageUrl} size="sm" />
            <StackedCell primary={row.original.name ?? 'Unnamed'} secondary={`ID ${row.original.id}`} />
          </div>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        meta: { sortKey: 'phone', mobileLabel: 'Contact' },
        cell: ({ row }) => (
          <StackedCell primary={row.original.phone ?? '—'} secondary={row.original.email ?? 'No email'} />
        ),
      },
      {
        id: 'workload',
        header: 'Workload',
        meta: { mobileLabel: 'Workload', align: 'center' },
        cell: ({ row }) =>
          row.original.activeTripCount > 0 ? (
            <Badge tone="brand" dot title="Trips currently assigned to this driver">
              {row.original.activeTripCount} active
            </Badge>
          ) : (
            <Badge tone="neutral">Available</Badge>
          ),
      },
      {
        id: 'createdAt',
        header: 'Added',
        meta: { sortKey: 'createdAt', mobileLabel: 'Added', hideOnMobile: true },
        cell: ({ row }) => formatDateTimeCompact(row.original.createdAt),
      },
      {
        id: 'enabled',
        header: 'Account',
        meta: { sortKey: 'enabled', mobileLabel: 'Account' },
        cell: ({ row }) => <StatusBadge status={row.original.enabled ? 'ACTIVE' : 'SUSPENDED'} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '170px' },
        cell: ({ row }) => (
          <RowActions>
            {row.original.enabled ? (
              <Button
                size="sm"
                variant="danger"
                iconLeft={<ShieldBan aria-hidden="true" />}
                onClick={() => setPending({ driver: row.original, action: 'suspend' })}
              >
                Suspend
              </Button>
            ) : (
              <Button
                size="sm"
                variant="subtle"
                iconLeft={<ShieldCheck aria-hidden="true" />}
                onClick={() => setPending({ driver: row.original, action: 'activate' })}
              >
                Reactivate
              </Button>
            )}
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminDriver>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Name', value: (row) => row.name ?? '' },
      { header: 'Phone', value: (row) => row.phone ?? '' },
      { header: 'Email', value: (row) => row.email ?? '' },
      { header: 'Active trips', value: (row) => row.activeTripCount },
      { header: 'Added', value: (row) => formatDateTimeCompact(row.createdAt, '') },
      { header: 'Phone verified', value: (row) => (row.phoneVerified ? 'Yes' : 'No') },
      { header: 'Status', value: (row) => (row.enabled ? 'Active' : 'Suspended') },
    ],
    [],
  )

  const page = driversQuery.data

  return (
    <>
      <PageHeader
        title="Drivers"
        description="Fleet accounts. A suspended driver cannot sign in and is withdrawn from every assignment control until reactivated."
        actions={
          <Button variant="primary" iconLeft={<Plus aria-hidden="true" />} onClick={() => setAddOpen(true)}>
            Add driver
          </Button>
        }
        meta={
          page ? (
            <span>
              {page.totalElements} {pluralize(page.totalElements, 'driver')} on record
            </span>
          ) : null
        }
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search by name, phone or email…"
              label="Search drivers"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-drivers"
              title="Drive For Me — Drivers"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchDrivers({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Account"
            value={table.filters.enabled}
            onChange={(value) => table.setFilter('enabled', value)}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Suspended' },
            ]}
          />
          <FilterDate
            label="From"
            value={table.filters.createdFrom}
            onChange={(value) => table.setFilter('createdFrom', value)}
            max={table.filters.createdTo || undefined}
          />
          <FilterDate
            label="To"
            value={table.filters.createdTo}
            onChange={(value) => table.setFilter('createdTo', value)}
            min={table.filters.createdFrom || undefined}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me drivers"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={driversQuery.isLoading}
          isFetching={driversQuery.isFetching && !driversQuery.isLoading}
          error={driversQuery.isError ? driversQuery.error : undefined}
          onRetry={() => void driversQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No drivers match these filters' : 'No drivers yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try clearing a filter or widening the date range.'
              : 'Add your first driver to start assigning trips.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : (
              <Button variant="primary" iconLeft={<Plus aria-hidden="true" />} onClick={() => setAddOpen(true)}>
                Add driver
              </Button>
            )
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="driver"
            disabled={driversQuery.isFetching}
          />
        ) : null}
      </Card>

      <AddDriverDialog open={addOpen} onClose={() => setAddOpen(false)} />

      <ConfirmDialog
        open={pending !== null}
        title={pending?.action === 'suspend' ? 'Suspend this driver?' : 'Reactivate this driver?'}
        message={
          pending ? (
            <>
              <strong>{pending.driver.name ?? `Driver #${pending.driver.id}`}</strong>
              {pending.action === 'suspend' ? (
                <>
                  {' '}
                  will be signed out and removed from driver assignment lists.
                  {pending.driver.activeTripCount > 0 ? (
                    <>
                      {' '}
                      They currently have <strong>{pending.driver.activeTripCount}</strong>{' '}
                      {pluralize(pending.driver.activeTripCount, 'active trip')} — reassign{' '}
                      {pending.driver.activeTripCount === 1 ? 'it' : 'them'} first to avoid leaving a passenger
                      without a driver.
                    </>
                  ) : null}
                </>
              ) : (
                ' will be able to sign in again and can be assigned new trips.'
              )}
            </>
          ) : (
            ''
          )
        }
        confirmLabel={pending?.action === 'suspend' ? 'Suspend driver' : 'Reactivate driver'}
        tone={pending?.action === 'suspend' ? 'danger' : 'primary'}
        loading={statusMutation.isPending}
        onConfirm={() => pending && statusMutation.mutate(pending)}
        onCancel={() => setPending(null)}
      />
    </>
  )
}
